<script setup lang="ts">
import { computed } from 'vue'
import CanvasPreviewSurface, { type PreviewDrawItem, type PreviewHotspotItem } from '@/components/workspace/CanvasPreviewSurface.vue'
import ImageOptionPickerField from '@/components/ImageOptionPickerField.vue'
import NumberStepperField from '@/components/NumberStepperField.vue'
import ResourceSelectField from '@/features/virtual-resource/components/ResourceSelectField.vue'
import type { SettingsPreviewButtonDraft, SettingsTitleTabProps } from '@/features/workspace/settings/types'

const props = defineProps<SettingsTitleTabProps>()

function handleTitleButtonEnabledChange(item: SettingsPreviewButtonDraft) {
  if (item.enabled && item.index === 0 && props.imageOptionPickerItems.length > 1) {
    item.index = 1
  }
}

const previewDrawItems = computed<PreviewDrawItem[]>(() =>
  props.titlePreviewButtons.map((item) => ({
    key: item.key,
    kind: 'image',
    style: item.style,
    imageUrl: item.url,
    placeholderText: item.label,
  })),
)

const previewHotspotItems = computed<PreviewHotspotItem[]>(() =>
  props.titlePreviewButtons.map((item) => ({
    key: item.key,
    style: {
      ...item.style,
      outline: props.activeTitleDragIndex === item.key ? '1px dashed var(--theme-accent)' : 'none',
      cursor: 'move',
    },
    ariaLabel: `${item.label}预览拖拽热区`,
    onPointerdown: (event: PointerEvent) => props.startTitleButtonDrag(item.key, event),
  })),
)
</script>

<template>
  <div
    v-if="portraitLayout"
    class="settings-page settings-portrait-page title-page-portrait"
  >
    <section class="setting-card settings-preview-card settings-portrait-preview title-preview-card title-portrait-preview">
      <CanvasPreviewSurface
        host-class="settings-preview-shell title-preview-shell"
        stage-class="settings-preview-stage title-preview-stage"
        :background-url="titleBackgroundUrl"
        :design-width="screenWidth"
        :design-height="screenHeight"
        background-empty-text="标题背景图"
        :draw-items="previewDrawItems"
        :hotspot-items="previewHotspotItems"
        :guide-active="previewGuideActive"
        @stage-resize="onPreviewStageResize"
      />
    </section>

    <div class="settings-portrait-props title-portrait-props">
      <section class="setting-card title-controls-card">
        <div class="card-title">标题按钮</div>

        <div class="settings-item-list">
          <div
            v-for="(item, index) in titleSetting.Buttons"
            :key="`title-button-portrait-${index}`"
            class="settings-item-card title-button-item"
          >
            <div class="settings-item-row settings-item-row--main">
              <label class="settings-item-enable">
                <input
                  v-model="item.enabled"
                  type="checkbox"
                  @change="handleTitleButtonEnabledChange(item)"
                />
              </label>
              <div class="settings-item-name">{{ item.label }}</div>
              <ImageOptionPickerField
                :model-value="item.index"
                :items="imageOptionPickerItems"
                :graphic-base-folder-path="imageOptionGraphicBaseFolderPath"
                :disabled="!item.enabled"
                title="选择标题按钮图片选项"
                @update:model-value="item.index = $event"
              />
            </div>

            <div class="settings-item-row settings-item-row--axis field-axis-grid field-axis-grid--plain title-button-axis-row">
              <NumberStepperField v-model="item.x" class="cell-input sm" :disabled="!item.enabled" />
              <NumberStepperField v-model="item.y" class="cell-input sm" :disabled="!item.enabled" />
            </div>
          </div>
        </div>
      </section>

      <section class="setting-card title-resource-card">
        <div class="title-resource-grid">
          <label class="field-block title-resource-field">
            <span class="field-label">封面图片</span>
            <ResourceSelectField
              :model-value="titleSetting.TitlePic.name"
              category="graphic"
              :base-folder-path="backgroundGraphicBaseFolderPath"
              display-mode="full"
              store-mode="relative"
              picker-title="选择标题背景图"
              @update:model-value="updateTitleBackgroundPic"
            />
          </label>

          <label class="field-block title-resource-field">
            <span class="field-label">封面音乐</span>
            <ResourceSelectField
              :model-value="titleSetting.TitleBGM.name"
              category="audio"
              :base-folder-path="musicBaseFolderPath"
              display-mode="full"
              store-mode="relative"
              picker-title="选择标题音乐"
              @update:model-value="updateTitleBgmPath"
            />
          </label>
        </div>
      </section>
    </div>
  </div>

  <div v-else class="settings-page title-page">
    <div class="title-grid">
      <div class="title-left-column">
        <section class="setting-card title-controls-card">
          <div class="card-title">标题按钮</div>

          <div class="settings-item-list">
            <div
              v-for="(item, index) in titleSetting.Buttons"
              :key="`title-button-${index}`"
              class="settings-item-card title-button-item"
            >
              <div class="settings-item-row settings-item-row--main">
                <label class="settings-item-enable">
                  <input
                    v-model="item.enabled"
                    type="checkbox"
                    @change="handleTitleButtonEnabledChange(item)"
                  />
                </label>
                <div class="settings-item-name">{{ item.label }}</div>
                <ImageOptionPickerField
                  :model-value="item.index"
                  :items="imageOptionPickerItems"
                  :graphic-base-folder-path="imageOptionGraphicBaseFolderPath"
                  :disabled="!item.enabled"
                  title="选择标题按钮图片选项"
                  @update:model-value="item.index = $event"
                />
              </div>

              <div class="settings-item-row settings-item-row--axis field-axis-grid field-axis-grid--plain title-button-axis-row">
                <NumberStepperField v-model="item.x" class="cell-input sm" :disabled="!item.enabled" />
                <NumberStepperField v-model="item.y" class="cell-input sm" :disabled="!item.enabled" />
              </div>
            </div>
          </div>
        </section>
      </div>

      <div class="title-right-column">
        <section class="setting-card title-resource-card">
          <div class="title-resource-grid">
            <label class="field-block title-resource-field">
              <span class="field-label">封面图片</span>
              <ResourceSelectField
                :model-value="titleSetting.TitlePic.name"
                category="graphic"
                :base-folder-path="backgroundGraphicBaseFolderPath"
                display-mode="full"
                store-mode="relative"
                picker-title="选择标题背景图"
                @update:model-value="updateTitleBackgroundPic"
              />
            </label>

            <label class="field-block title-resource-field">
              <span class="field-label">封面音乐</span>
              <ResourceSelectField
                :model-value="titleSetting.TitleBGM.name"
                category="audio"
                :base-folder-path="musicBaseFolderPath"
                display-mode="full"
                store-mode="relative"
                picker-title="选择标题音乐"
                @update:model-value="updateTitleBgmPath"
              />
            </label>
          </div>
        </section>

        <section class="setting-card settings-preview-card title-preview-card">
          <CanvasPreviewSurface
            host-class="settings-preview-shell title-preview-shell"
            stage-class="settings-preview-stage title-preview-stage"
            :background-url="titleBackgroundUrl"
            :design-width="screenWidth"
            :design-height="screenHeight"
            background-empty-text="标题背景图"
            :draw-items="previewDrawItems"
            :hotspot-items="previewHotspotItems"
            :guide-active="previewGuideActive"
            @stage-resize="onPreviewStageResize"
          />
        </section>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import './shared.css';

.settings-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  padding-top: 4px;
  box-sizing: border-box;
}

.title-grid {
  display: grid;
  grid-template-columns: var(--settings-layout-left-width) minmax(0, 1fr);
  gap: 8px;
  height: 100%;
  min-height: 0;
  align-items: stretch;
}

.title-left-column {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
}

.title-right-column {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
}

.title-resource-card {
  flex-shrink: 0;
}

.title-resource-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  min-width: 0;
}

.title-controls-card,
.title-resource-field :deep(.resource-select-input),
.title-button-item :deep(.image-option-picker-input) {
  background: var(--theme-bg-card);
  color: var(--theme-text-heading);
  border: 1px solid var(--theme-border);
}

.title-resource-field :deep(.resource-select-trigger),
.title-button-item :deep(.image-option-picker-trigger) {
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-card);
  color: var(--theme-text-heading);
}

.title-resource-field :deep(.resource-select-trigger:hover),
.title-button-item :deep(.image-option-picker-trigger:hover) {
  border-color: var(--theme-accent);
}

.title-preview-button {
  position: absolute;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: move;
  user-select: none;
  touch-action: none;
  box-sizing: border-box;
}

.title-preview-button img {
  display: block;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  pointer-events: none;
}

.title-preview-button--dragging {
  border: 1px dashed var(--theme-accent);
}

.title-button-axis-row {
  padding-left: 0;
}

.title-page-portrait {
  height: 100%;
}

.title-portrait-preview {
  min-height: 0;
  overflow: hidden;
}

.title-portrait-props .setting-card {
  flex: 0 0 auto;
}

@media (max-width: 1180px) and (orientation: landscape) {
  .title-grid {
    grid-template-columns: var(--settings-layout-left-width) minmax(0, 1fr);
  }

  .title-resource-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 1200px) and (orientation: portrait) {
  .title-grid {
    grid-template-columns: 1fr;
  }

  .title-resource-grid {
    grid-template-columns: 1fr;
  }
}
</style>
